'use client';

import Image from 'next/image';
import { Icon } from './icon';
import { useGeneratedImage } from './use-generated-image';
import styles from './media-thumb.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type ThumbSize = 'sm' | 'md' | 'lg';

interface MediaThumbProps {
  /** Resolved image URL, or null when the generation job has not finished. */
  imageUrl: string | null;
  /**
   * Alt text. Pass '' for a decorative thumb that sits beside its own label —
   * a cross-link already names the dish in its title, so announcing the photo
   * as well would read the same recipe twice.
   */
  alt: string;
  size?: ThumbSize;
  /** Enables polling for a still-generating image. Omit to render static. */
  recipeId?: string;
  variantType?: string | null;
  /** Accessible label for the pending state; omit when the thumb is decorative. */
  pendingLabel?: string;
  className?: string;
}

/**
 * MediaThumb — a square recipe photograph with the three states a thumbnail
 * needs: image, pending, and cross-fade on arrival (#646).
 *
 * Extracted rather than redrawn. The stripe-fill-plus-spinner treatment existed
 * only inside `recipe-image.module.css`, welded to the full-bleed hero; the
 * remix card needed the same thing at 64px, and copying it would have made two
 * definitions of one idea. `RecipeImage` keeps its hero layout but now shares
 * this component's polling hook and hatch token.
 *
 * Sized from `--thumb-*` — never a raw px — so every thumbnail in the app is
 * one of three known sizes.
 */
export function MediaThumb({
  imageUrl: initialImageUrl,
  alt,
  size = 'md',
  recipeId,
  variantType,
  pendingLabel,
  className,
}: MediaThumbProps) {
  const { imageUrl, isPending, justArrived } = useGeneratedImage(
    initialImageUrl,
    recipeId,
    variantType,
  );

  return (
    <span className={cx(styles.thumb, styles[size], className)}>
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={alt}
          fill
          sizes="72px"
          className={cx(styles.image, justArrived && styles.imageArrived)}
        />
      ) : (
        <span
          className={styles.placeholder}
          role={isPending && pendingLabel ? 'status' : undefined}
          aria-live={isPending && pendingLabel ? 'polite' : undefined}
          aria-label={isPending ? pendingLabel : undefined}
        >
          <Icon
            name={isPending ? 'loading' : 'fork-knife'}
            className={isPending ? styles.spinner : styles.icon}
            strokeWidth={isPending ? 1.5 : 1}
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}
